import React, { FC, useEffect } from 'react'
import { Redirect, useLocation } from 'react-router'

import {
  useGetProjectSharesQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { hasGraphQLErrorCode } from 'graphql/utils/graphqlErrors'

import DocumentItemsLayout from 'modules/shares/components/DocumentItemsLayout'
import { handleFetchMore } from 'components/InfiniteList'
import { DocumentHead, ErrorMessage } from '@sketch/components'
import ProjectHeader from '../ProjectHeader'
import { DocumentItemDropdown } from 'modules/shares/components/DocumentItemDropdown'

// Containers
import { useBanner } from 'containers/Banner'

// Icons
import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64-svg'

import EmptyState from 'modules/shares/components/EmptyState'
import { routes } from '@sketch/constants'

// Utils
import { useOnEvent } from '@sketch/utils'
import { BannerButton } from './ProjectShares.styles'
import { useModalContext } from 'containers/ModalManager'
import { ShareTrashedModal } from 'modules/shares/components/ShareTrashedModal'
import RestoreProject from 'modules/projects/modals/RestoreProject'
import ProjectRequestAccess from '../ProjectRequestAccess'
import { ErrorHandler } from '@sketch/tracing'

// Helpers
const EMPTY_STATE_COPY = {
  standard: {
    editor: {
      title: 'No documents in this project yet',
      description:
        'Add a document by saving it in the Mac app — or move an existing Workspace document here.',
    },
    viewer: {
      title: 'No documents in this project yet',
      description: 'Only Editors can create documents.',
    },
  },
  drafts: {
    editor: {
      title: 'No documents',
      description: 'No documents added to My Drafts yet.',
    },
    /**
     * Viewers can also have a drafts folder in some scenarios.
     * For example: when a user is a normal editor with a Drafts folder but gets downgraded to viewer,
     * it keeps the drafts folder even if it is empty.
     */
    viewer: {
      title: "You haven't added anything to My Drafts",
      description: 'Only Editors can create documents.',
    },
  },
}

const PENDING_PROJECT_REQUEST = 'PENDING_REQUEST'
const REQUEST_ACCESS_REASON = [
  'RESTRICTED_TO_PROJECT_MEMBERS',
  PENDING_PROJECT_REQUEST,
]

function getEmptyStateContent(
  isDraftsProject: boolean,
  userIsEditor: boolean,
  name: string,
  deleted: boolean
) {
  const projectType = isDraftsProject ? 'drafts' : 'standard'

  if (deleted) {
    return {
      title: 'No documents in deleted project',
      description: `Restore “${name}” if you want to access or add documents to it.`,
    }
  }

  const userType = userIsEditor ? 'editor' : 'viewer'

  return EMPTY_STATE_COPY[projectType][userType]
}

export interface ProjectSharesProps {
  projectId: string
  workspace: WorkspaceMinimalFragment
  search?: string
  userIsEditor: boolean
  userCanRenameProject: boolean
}

const entriesPath = ['project', 'shares', 'entries']

const ProjectShares: FC<ProjectSharesProps> = props => {
  const {
    workspace,
    projectId,
    search,
    userIsEditor,
    userCanRenameProject,
  } = props

  const location = useLocation()
  const { showBanner } = useBanner()
  const { showModal } = useModalContext()

  const { identifier: workspaceId } = workspace

  const variables = {
    shortId: projectId,
    after: null,
    search: {
      name: search || null,
      isCurrentVersionDownloadable: null,
    },
  }

  const {
    data,
    networkStatus,
    fetchMore,
    error,
    refetch,
  } = useGetProjectSharesQuery({
    variables,
    shouldInvalidatePrevious: (prev, curr) => prev?.shortId !== curr?.shortId,
  })

  useOnEvent(
    'workspaceShareRefresh',
    ({ workspaceIdentifier, projectShortId }) => {
      if (workspaceIdentifier !== workspaceId || projectShortId !== projectId) {
        // Not the workspace visible ignore then
        return
      }

      refetch()
    }
  )

  const project = data && data.project

  useEffect(() => {
    if (project?.deletedAt) {
      const { dismissBanner } = showBanner({
        type: 'information',
        message: (
          <>
            The <b>“{project.name}”</b> project was deleted.{' '}
            <BannerButton
              onClick={() => {
                showModal(RestoreProject, { project, workspaceId })
              }}
            >
              Restore the project
            </BannerButton>
            , to edit its documents.
          </>
        ),
      })

      return dismissBanner
    }
  }, [showModal, showBanner, project, workspaceId])

  if (error) {
    const forbiddenError = hasGraphQLErrorCode(error, 'FORBIDDEN')

    if (
      forbiddenError &&
      REQUEST_ACCESS_REASON.includes(forbiddenError.extensions?.reason)
    ) {
      return (
        <ProjectRequestAccess
          projectShortId={projectId}
          hasPendingRequest={
            forbiddenError.extensions!.reason === PENDING_PROJECT_REQUEST
          }
        />
      )
    }

    if (hasGraphQLErrorCode(error, 'NOT_FOUND')) {
      return (
        <ErrorMessage
          icon={<WarningTriangle />}
          title="Project not available"
          description="The Project you're looking for might have been deleted, or you don't have the right permissions to view it."
        />
      )
    }

    return (
      <ErrorMessage.Generic title="Something went wrong while loading shares" />
    )
  }

  /* Redirect to the Trash Project View if the project has been soft-deleted */
  if (
    project?.deletedAt &&
    location.pathname ===
      routes.WORKSPACE_PROJECT.create({ workspaceId, projectId })
  ) {
    return (
      <Redirect
        to={routes.WORKSPACE_TRASH_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }

  /* Redirect to the Project View if the project has been restored */
  if (
    !project?.deletedAt &&
    location.pathname ===
      routes.WORKSPACE_TRASH_PROJECT.create({ workspaceId, projectId })
  ) {
    return (
      <Redirect
        to={routes.WORKSPACE_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }

  const shares = (project && project.shares.entries) || []
  const after = data?.project.shares.meta.after as string | null
  const loadMoreHandler = handleFetchMore(fetchMore, entriesPath, {
    after,
  })

  const loading = networkStatus < 3
  const isDraftsProject = data?.project.type === 'PERSONAL_DRAFTS'

  if (!loading && shares.length === 0) {
    const content = getEmptyStateContent(
      isDraftsProject,
      userIsEditor,
      project!.name,
      !!project?.deletedAt
    )

    return (
      <>
        {!isDraftsProject && (
          <ProjectHeader
            project={project!}
            workspace={workspace}
            workspaceAccessLevel={project?.workspaceAccessLevel!}
            userCanRenameAndDeleteProject={userCanRenameProject || userIsEditor}
          />
        )}
        <EmptyState
          search={search}
          title={content.title}
          description={content.description}
          icon={isDraftsProject ? 'notes' : 'document'}
        />
      </>
    )
  }

  const totalShareCount = data?.project.shares.meta.totalCount || 0

  return (
    <>
      <DocumentHead title={isDraftsProject ? 'Drafts' : project?.name} />
      <DocumentItemsLayout
        loading={loading}
        shares={shares}
        renderDropdown={({ identifier }) => {
          const share = shares.find(x => x.identifier === identifier)
          if (!share) {
            ErrorHandler.shouldNeverHappen(
              'share should always be present rendering the ProjectDocumentDropdown'
            )
            return null
          }
          return (
            <DocumentItemDropdown
              share={share}
              workspaceIdentifier={workspace.identifier}
            />
          )
        }}
        onLoadMore={loadMoreHandler}
        showProjectName={false}
        totalShareCount={totalShareCount}
        action={share => {
          if (share.deletedAt) {
            return () => showModal(ShareTrashedModal, { share })
          }

          return 'link'
        }}
        header={
          <ProjectHeader
            project={project!}
            workspace={workspace}
            workspaceAccessLevel={project?.workspaceAccessLevel!}
            userCanRenameAndDeleteProject={userCanRenameProject}
          />
        }
      />
    </>
  )
}

export default ProjectShares
